import * as React from 'react'
import { useMemo } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { getCompanyPaidLabel } from 'modules/expenses/components/expenses/New/PerDiemWizard/companyPaidUtils'
import ExclusionToggleButton, {
  ExclusionType
} from 'modules/expenses/components/expenses/New/PerDiemWizard/GermanyModal/_ExclusionToggleButton'
import { usePerDiemWizard } from 'modules/expenses/components/expenses/New/PerDiemWizard/form/context'
import { DayExclusion } from 'modules/expenses/components/expenses/New/PerDiemWizard/form/types'
import { useCalculatePerDiem } from 'modules/expenses/components/expenses/New/PerDiemWizard/hooks/useCalculatePerDiem'

// Helper to compare dates by day (ignoring time)
const isSameDay = (date1: Date, date2: Date): boolean =>
  date1.getFullYear() === date2.getFullYear() &&
  date1.getMonth() === date2.getMonth() &&
  date1.getDate() === date2.getDate()

interface DayRowProps {
  date: Date
  dayNumber: number
  exclusion: DayExclusion
  onToggle: (type: ExclusionType) => void
  isLastDay: boolean
}

const DayRow = ({ date, dayNumber, exclusion, onToggle, isLastDay }: DayRowProps) => {
  const formattedDate = moment(date).format('ddd, MMM D')
  const companyPaidLabel = getCompanyPaidLabel(exclusion)

  return (
    <Box flexDirection="column" gap="s8">
      <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
        <Text.Normal size="200" weight="medium" color="grey1000">
          {formattedDate}
        </Text.Normal>
        <Text.Normal size="200" color="grey800">
          {i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.day_label', {
            day: dayNumber
          })}
        </Text.Normal>
      </Box>
      <Box flexDirection="row" justifyContent="spaceBetween">
        <ExclusionToggleButton
          type="breakfastProvided"
          isActive={exclusion.breakfastProvided}
          onToggle={() => onToggle('breakfastProvided')}
        />
        <ExclusionToggleButton
          type="lunchProvided"
          isActive={exclusion.lunchProvided}
          onToggle={() => onToggle('lunchProvided')}
        />
        <ExclusionToggleButton
          type="dinnerProvided"
          isActive={exclusion.dinnerProvided}
          onToggle={() => onToggle('dinnerProvided')}
        />
        <ExclusionToggleButton
          type="accommodationProvided"
          isActive={exclusion.accommodationProvided}
          onToggle={() => onToggle('accommodationProvided')}
          disabled={isLastDay}
        />
      </Box>
      {companyPaidLabel && (
        <Text.Normal size="200" color="grey700">
          {companyPaidLabel}
        </Text.Normal>
      )}
    </Box>
  )
}

const getDaysBetween = (startDate: Date, endDate: Date) => {
  const result: Date[] = []
  const current = new Date(startDate)
  current.setHours(0, 0, 0, 0)
  const end = new Date(endDate)
  end.setHours(0, 0, 0, 0)

  while (current <= end) {
    result.push(new Date(current))
    current.setDate(current.getDate() + 1)
  }

  return result
}

const CompanyPaidItems = () => {
  const { data, updateData, setCalculation } = usePerDiemWizard()
  const { calculate } = useCalculatePerDiem()
  const { employee } = useCurrentSession()

  const employeeId = employee?.id

  // Generate list of days between start and end date
  const days = useMemo(() => {
    if (!data.startDate || !data.endDate) return []

    return getDaysBetween(data.startDate, data.endDate)
  }, [data.startDate, data.endDate])

  // Get or create exclusion for a day
  const getExclusion = (date: Date): DayExclusion => {
    const existing = data.dayExclusions.find((e) => isSameDay(e.date, date))
    return (
      existing || {
        date,
        breakfastProvided: false,
        lunchProvided: false,
        dinnerProvided: false,
        accommodationProvided: false
      }
    )
  }

  // Trigger per diem recalculation
  const triggerCalculation = async (newExclusions: DayExclusion[]) => {
    if (!employeeId || !data.startDate || !data.endDate) return

    const hasValidDestinations = data.destinations.some(
      (dest) => dest.locationData.country && dest.arrivalDatetime
    )
    if (!hasValidDestinations) return

    setCalculation({ isCalculating: true })

    const result = await calculate({
      employeeId,
      startDate: data.startDate,
      endDate: data.endDate,
      destinations: data.destinations,
      dayExclusions: newExclusions
    })

    setCalculation(result)
  }

  // Toggle a specific exclusion type for a day
  const toggleExclusion = (date: Date, type: ExclusionType) => {
    const current = getExclusion(date)
    const updated: DayExclusion = {
      ...current,
      date,
      [type]: !current[type]
    }

    const newExclusions = data.dayExclusions.filter((e) => !isSameDay(e.date, date))

    // Only add if any exclusion is true
    if (
      updated.breakfastProvided ||
      updated.lunchProvided ||
      updated.dinnerProvided ||
      updated.accommodationProvided
    ) {
      newExclusions.push(updated)
    }

    updateData({ dayExclusions: newExclusions })
    triggerCalculation(newExclusions)
  }

  // Toggle all of a specific type
  const toggleAll = (type: ExclusionType) => {
    const allSelected = isAllSelected(type)

    const newExclusions = days.map((date) => {
      const current = getExclusion(date)
      return {
        ...current,
        date,
        [type]: !allSelected
      }
    })

    // Last day cannot have overnight
    const lastExclusion = newExclusions.at(-1)
    if (type === 'accommodationProvided' && lastExclusion) {
      lastExclusion.accommodationProvided = false
    }

    // Filter out exclusions where all values are false
    const filteredExclusions = newExclusions.filter(
      (e) => e.breakfastProvided || e.lunchProvided || e.dinnerProvided || e.accommodationProvided
    )

    updateData({ dayExclusions: filteredExclusions })
    triggerCalculation(filteredExclusions)
  }

  const isAllSelected = (type: ExclusionType): boolean => {
    if (days.length === 0) return false
    const daysToCheck = type === 'accommodationProvided' ? days.slice(0, -1) : days
    return daysToCheck.length > 0 && daysToCheck.every((date) => getExclusion(date)[type])
  }

  return (
    <Box flexDirection="column" gap="s24" padding="s24">
      <Box flexDirection="column" gap="s8">
        <Text.Normal size="300" weight="semibold" color="grey1000">
          {i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.title')}
        </Text.Normal>
        <Text.Normal size="200" color="grey700">
          {i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.description')}
        </Text.Normal>
      </Box>

      <Box flexDirection="row" gap="s8">
        <Button
          type={isAllSelected('breakfastProvided') ? 'positive' : 'secondary'}
          onClick={() => toggleAll('breakfastProvided')}
        >
          {i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.all_breakfasts')}
        </Button>
        <Button
          type={isAllSelected('lunchProvided') ? 'positive' : 'secondary'}
          onClick={() => toggleAll('lunchProvided')}
        >
          {i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.all_lunches')}
        </Button>
        <Button
          type={isAllSelected('dinnerProvided') ? 'positive' : 'secondary'}
          onClick={() => toggleAll('dinnerProvided')}
        >
          {i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.all_dinners')}
        </Button>
        <Button
          type={isAllSelected('accommodationProvided') ? 'positive' : 'secondary'}
          onClick={() => toggleAll('accommodationProvided')}
        >
          {i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.all_overnights')}
        </Button>
      </Box>

      <Box flexDirection="column" gap="s16">
        {days.map((date, index) => (
          <DayRow
            key={date.toISOString()}
            date={date}
            dayNumber={index + 1}
            exclusion={getExclusion(date)}
            onToggle={(type) => toggleExclusion(date, type)}
            isLastDay={index === days.length - 1}
          />
        ))}
      </Box>

      {days.length === 0 && (
        <Box padding="s24" justifyContent="center" alignItems="center">
          <Text.Normal size="200" color="grey600">
            {i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.no_days')}
          </Text.Normal>
        </Box>
      )}
    </Box>
  )
}

export default CompanyPaidItems
